import React from 'react';
import { 
  Box, 
  VStack, 
  Heading, 
  Text, 
  Button,
  HStack,
  Code
} from '@chakra-ui/react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      isExpanded: false
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // You can also log the error to an error reporting service here
    // logErrorToService(error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  toggleDetails = () => {
    this.setState(prevState => ({
      isExpanded: !prevState.isExpanded
    }));
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo, isExpanded } = this.state;
      const { fallback } = this.props;

      // Custom fallback UI if provided
      if (fallback) {
        return fallback(error, this.handleReload);
      }

      // Default error UI matching your design system
      return (
        <Box py={20} px={4} minH="60vh">
          <VStack spacing={8} maxW="4xl" mx="auto" textAlign="center">
            {/* Error Icon */}
            <Box fontSize="6xl" opacity={0.7}>
              ⚠️
            </Box>

            {/* Error Message */}
            <VStack spacing={4}>
              <Heading 
                size="2xl" 
                textTransform="uppercase" 
                letterSpacing="wide"
                color="red.400"
              >
                SOMETHING WENT WRONG
              </Heading>
              <Text fontSize="lg" color="gray.300" maxW="2xl" lineHeight="1.6">
                We apologize for the inconvenience. An unexpected error occurred 
                while loading this page. Our team has been notified.
              </Text>
            </VStack>

            {/* Action Buttons */}
            <HStack spacing={4} flexWrap="wrap" justify="center">
              <Button
                bg="green.500"
                color="white"
                _hover={{ bg: "green.600" }}
                onClick={this.handleReload}
                textTransform="uppercase"
                letterSpacing="wide"
              >
                Reload Page
              </Button>
              <Button
                variant="outline"
                borderColor="gray.600"
                color="white"
                _hover={{ borderColor: "green.500", color: "green.500" }}
                onClick={this.handleGoHome}
                textTransform="uppercase"
                letterSpacing="wide"
              >
                Go Home
              </Button>
            </HStack>

            {/* Technical Details (Collapsible) */}
            {process.env.NODE_ENV === 'development' && (
              <VStack spacing={4} w="100%">
                <Button
                  variant="ghost"
                  size="sm"
                  color="gray.400"
                  _hover={{ color: "gray.300" }}
                  onClick={this.toggleDetails}
                >
                  {isExpanded ? 'Hide' : 'Show'} Technical Details
                </Button>
                
                {isExpanded && (
                  <Box 
                    bg="gray.900" 
                    p={6} 
                    borderRadius="md" 
                    border="1px solid" 
                    borderColor="gray.700"
                    w="100%"
                    textAlign="left"
                  >
                    <VStack spacing={4} align="start">
                      <VStack align="start" spacing={2}>
                        <Text fontSize="sm" fontWeight="bold" color="red.400">
                          Error Message:
                        </Text>
                        <Code 
                          p={2} 
                          bg="red.900" 
                          color="red.200" 
                          borderRadius="md"
                          fontSize="xs"
                          whiteSpace="pre-wrap"
                          w="100%"
                        >
                          {error && error.toString()}
                        </Code>
                      </VStack>

                      {errorInfo && errorInfo.componentStack && (
                        <VStack align="start" spacing={2}>
                          <Text fontSize="sm" fontWeight="bold" color="orange.400">
                            Component Stack:
                          </Text>
                          <Code 
                            p={2} 
                            bg="orange.900" 
                            color="orange.200" 
                            borderRadius="md"
                            fontSize="xs"
                            whiteSpace="pre-wrap"
                            w="100%"
                            maxH="200px"
                            overflowY="auto"
                          >
                            {errorInfo.componentStack}
                          </Code>
                        </VStack>
                      )}
                    </VStack>
                  </Box>
                )}
              </VStack>
            )}

            {/* Help Text */}
            <VStack spacing={2}>
              <Text fontSize="sm" color="gray.400">
                If this problem persists, please contact our support team.
              </Text>
              <HStack spacing={1} fontSize="sm" color="gray.500">
                <Text>Error ID:</Text>
                <Code fontSize="xs" bg="gray.800" px={2}>
                  {Date.now().toString(36).toUpperCase()}
                </Code>
              </HStack>
            </VStack>
          </VStack>
        </Box>
      );
    }

    // No error, render children normally
    return this.props.children;
  }
}

// Higher-order component for easier usage
export const withErrorBoundary = (Component, fallback) => {
  const WrappedComponent = (props) => (
    <ErrorBoundary fallback={fallback}>
      <Component {...props} />
    </ErrorBoundary>
  );
  
  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  return WrappedComponent;
};

// Specialized error boundaries for different contexts
export const PageErrorBoundary = ({ children, pageName }) => (
  <ErrorBoundary
    fallback={(error, reload) => (
      <Box py={20} px={4} minH="60vh">
        <VStack spacing={8} maxW="3xl" mx="auto" textAlign="center">
          <Box fontSize="5xl" opacity={0.7}>🔧</Box>
          <VStack spacing={4}>
            <Heading size="xl" textTransform="uppercase" letterSpacing="wide">
              {pageName} TEMPORARILY UNAVAILABLE
            </Heading>
            <Text color="gray.300" lineHeight="1.6">
              We're experiencing technical difficulties with this page. 
              Please try again in a few moments.
            </Text>
          </VStack>
          <HStack spacing={4}>
            <Button
              bg="green.500"
              color="white"
              _hover={{ bg: "green.600" }}
              onClick={reload}
              size="sm"
            >
              Try Again
            </Button>
            <Button
              variant="outline"
              borderColor="gray.600"
              color="white"
              _hover={{ borderColor: "green.500", color: "green.500" }}
              onClick={() => window.location.href = '/'}
              size="sm"
            >
              Go Home
            </Button>
          </HStack>
        </VStack>
      </Box>
    )}
  >
    {children}
  </ErrorBoundary>
);

export default ErrorBoundary;